import * as React from "react";
import { cn } from "@/lib/utils";
import { ArrowLeft, ArrowRight, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Avatar } from "./avatar";
import { Badge } from "./badge";
import { Button } from "./button";

interface ProfileHeroProps extends React.HTMLAttributes<HTMLDivElement> {
  avatarUrl?: string;
  avatarAlt?: string;
  name: string;
  subtitle?: string;
  status?: {
    label: string;
    variant?: "default" | "success" | "warning" | "danger" | "secondary";
  };
  rating?: {
    value: number;
    count?: number;
  };
  gradientFrom?: string;
  gradientTo?: string;
  onBack?: () => void;
  actions?: React.ReactNode;
}

const ProfileHero = React.forwardRef<HTMLDivElement, ProfileHeroProps>(
  (
    {
      avatarUrl,
      avatarAlt,
      name,
      subtitle,
      status,
      rating,
      gradientFrom = "from-primary/80",
      gradientTo = "to-primary",
      onBack,
      actions,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const { t, i18n } = useTranslation();
    const BackIcon = i18n.dir() === 'rtl' ? ArrowRight : ArrowLeft;
    return (
      <div
        ref={ref}
        className={cn(
          "relative overflow-hidden rounded-xl",
          className
        )}
        {...props}
      >
        {/* Gradient Background */}
        <div
          className={cn(
            "absolute inset-0 bg-gradient-to-l",
            gradientFrom,
            gradientTo
          )}
        />

        {/* Content */}
        <div className="relative z-10 p-6 sm:p-8">
          {/* Back Button */}
          {onBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="absolute start-4 top-4 text-primary-foreground/80 hover:bg-primary-foreground/10 hover:text-primary-foreground"
              aria-label={t('common.back')}
            >
              <BackIcon className="me-1 h-4 w-4" aria-hidden="true" />
              <span>{t('common.back')}</span>
            </Button>
          )}

          <div className="flex flex-col sm:flex-row items-center gap-6 text-white">
            {/* Avatar */}
            <div className="relative">
              <Avatar
                src={avatarUrl}
                alt={avatarAlt || name}
                className="h-24 w-24 sm:h-32 sm:w-32 border-4 border-white/20 shadow-xl"
              />
              {status && (
                <Badge
                  variant={status.variant || "default"}
                  className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap"
                >
                  {status.label}
                </Badge>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 text-center sm:text-right">
              <h1 className="text-2xl sm:text-3xl font-bold">{name}</h1>
              {subtitle && (
                <p className="text-white/80 mt-1 text-lg">{subtitle}</p>
              )}
              {rating && (
                <div className="flex items-center justify-center sm:justify-start gap-2 mt-3">
                  <div className="flex items-center gap-1 bg-white/20 rounded-full px-3 py-1">
                    <Star className="h-4 w-4 fill-warning text-warning" />
                    <span className="font-semibold">{rating.value.toFixed(1)}</span>
                    {rating.count !== undefined && (
                      <span className="text-white/70 text-sm">
                        ({rating.count})
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons Slot */}
              {actions && (
                <div className="mt-4 flex flex-wrap items-center justify-center sm:justify-start gap-2">
                  {actions}
                </div>
              )}
            </div>
          </div>

          {/* Additional Content */}
          {children}
        </div>
      </div>
    );
  }
);
ProfileHero.displayName = "ProfileHero";

export { ProfileHero };
export type { ProfileHeroProps };
